import React from 'react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import HorizontalStepper, { ArtoslibHorizontalStepper } from '../../components/steppers/HorizontalStepper'
import {
    Alert,
    Fade,
    Modal,
    ThemeProvider,
    CircularProgress,
    Typography,
    Box,
    Tooltip,
    IconButton,
    Snackbar,
    AlertTitle,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import InfoIcon from '@mui/icons-material/Info'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import '../../App.css'
import {
    processTemplate,
    ingestTemplateTables,
    listAllFiles,
    ingestTemplate,
    removeOrgId,
    formatFilename,
    ingestSourceDocuments,
    analyzeExampleDocument,
    syncRemoveFileExtension,
    createGenericMRTOutlineAsync,
    ingestTabulatedSummaryExamples,
    getDocumentTypesForOrganization
} from '../../utils/utils'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { useAuthInfo } from '@propelauth/react'

import MainButton from '../../components/MainButton'
import SelectInput from '../../components/inputs/select'

// Template Creation Step Components
import TemplateDetailsComponent from './steps/TemplateDetailsComponent.js'
import TrainingSetCreationComponent from './steps/TrainingSetCreationComponent.js'
import ReferenceFileComponent from './steps/ReferenceFileComponent.js'
import TemplateReviewComponent from './steps/TemplateReviewComponent.js'
import CustomStepper from '../../components/CustomStepper'

// Contexts
import { useDemo } from '../../contexts/DemoContext'
import { useStyleGuide } from '../../contexts/StyleGuideContext'
import { TemplateCreationProvider, useTemplateCreation } from '../../contexts/TemplateCreationContext'

import SourceSelectionModal from '../CreateDocumentPage/steps/SourceSelectionModal'
import FileSelectionComponent from '../../components/FileSelectionComponent'

import { v4 as uuidv4 } from 'uuid'

import { integrations } from '../../utils/integrations.js'

import GeneratingTemplateModal from './steps/modals/GeneratingTemplateModal'

const dev_mode = false
const START_STEP = dev_mode ? 1 : 0

export const documentTypeList = [
    {
        label: 'CSR',
        value: 'CSR',
    },
    {
        label: 'Written Summary',
        value: 'Written Summary',
    },
    {
        label: 'Tabulated Summary',
        value: 'Tabulated Summary',
    },
    {
        label: 'CSR with Shell Table(s)',
        value: 'CSR with Shell Table(s)',
    },
]

const oldStyleGuides = [
    {
        label: 'Pfizer Lean Writing Style Guide',
        value: 'Pfizer Lean Writing Style Guide',
    },
    {
        label: 'Style Guide 2',
        value: 'Style Guide 2',
    },
    {
        label: 'Style Guide 3',
        value: 'Style Guide 3',
    },
    {
        label: 'Style Guide 4',
        value: 'Style Guide 4',
    },
]

const templateTypeList = [
    {
        value: "IB",
        label: "IB",
    },
    {
        value: "CSR",
        label: "CSR",
    },
    {
        value: "Written Summary",
        label: "Written Summary",
    },
    {
        value: "Pharmacology Tabulated Summary",
        label: "Pharmacology Tabulated Summary",
    },
    {
        value: "Toxicology Tabulated Summary",
        label: "Toxicology Tabulated Summary",
    },
    {
        value: "Pharmacokinetics Tabulated Summary",
        label: "Pharmacokinetics Tabulated Summary",
    }
]

const templateTypeList2 = [
    {
        value: "Shell CSR",
        label: "Shell CSR",
    },
    {
        value: "NCO",
        label: "NCO",
    }    
]

// const integrations = [
//     { name: 'Artos', status: 'Select from Library', img: LogoPNG },
// ]

// Input Selection Component
const InputSelectionComponent = ({
    onNextClick1,
    onBackClick,
    sourceClick,
    multipleFileList,
    removeFile,
    orgId,
}) => {
    // const authInfo = useAuthInfo()
    // const { isDemo } = useDemo()

    return (
        <div className="upload-container">
            <div className="input-container">
                <p
                    className="secondary-text"
                    style={{ fontWeight: 'bold', color: 'black' }}
                >
                    Upload <i>Input File(s)</i> associated with the Template
                    File:
                </p>
            </div>
            <FileSelectionComponent
                sourceClick={sourceClick}
                integrations={integrations}
                multipleFileList={multipleFileList}
                removeFile={removeFile}
                orgId={orgId}
            />
            <div className="button-container">
                <MainButton
                    onClick={onBackClick}
                    text="Back"
                    width="100px"
                    backgroundColor="#ffffff"
                    fontColor="#1f477d"
                    className="main-button"
                    border="2px solid #1f477d"
                    style={{ marginRight: '10px' }}
                />
                <MainButton
                    onClick={onNextClick1}
                    text="Next"
                    width="100px"
                    backgroundColor="#1f477d"
                    fontColor="#ffffff"
                    className="main-button"
                />
            </div>
        </div>
    )
}

// Output Selection Component
const OutputSelectionComponent = ({
    onNextClick1,
    onBackClick,
    sourceClick,
    multipleFileList,
    removeFile,
    orgId,
}) => {
    // const authInfo = useAuthInfo()
    // const { isDemo } = useDemo()

    return (
        <div className="upload-container">
            <div className="input-container">
                <p
                    className="secondary-text"
                    style={{ fontWeight: 'bold', color: 'black' }}
                >
                    Upload the <i>Output File</i> associated with the Input
                    File(s):
                </p>
            </div>
            <FileSelectionComponent
                sourceClick={sourceClick}
                integrations={integrations}
                multipleFileList={multipleFileList}
                removeFile={removeFile}
                orgId={orgId}
            />

            <div className="button-container">
                <MainButton
                    onClick={onBackClick}
                    text="Back"
                    width="100px"
                    backgroundColor="#ffffff"
                    fontColor="#1f477d"
                    className="main-button"
                    border="2px solid #1f477d"
                    style={{ marginRight: '10px' }}
                />
                <MainButton
                    onClick={onNextClick1}
                    text="Next"
                    width="100px"
                    backgroundColor="#1f477d"
                    fontColor="#ffffff"
                    className="main-button"
                />
            </div>
        </div>
    )
}

// Lean Writing Template Selection Component
const LeanWritingTemplateSelectionComponent = ({
    onNextClick1,
    onBackClick,
    sourceClick,
    multipleFileList,
    removeFile,
    orgId,
}) => {
    return (
        <div className="upload-container">
            <div className="input-container">
                <p
                    className="secondary-text"
                    style={{ fontWeight: 'bold', color: 'black' }}
                >
                    Upload a <i>Style Guide</i> or <i>Lean Writing Template</i>:
                </p>
            </div>
            <FileSelectionComponent
                sourceClick={sourceClick}
                integrations={integrations}
                multipleFileList={multipleFileList}
                removeFile={removeFile}
                orgId={orgId}
            />

            <div className="button-container">
                <MainButton
                    onClick={onBackClick}
                    text="Back"
                    width="100px"
                    backgroundColor="#ffffff"
                    fontColor="#1f477d"
                    className="main-button"
                    border="2px solid #1f477d"
                    style={{ marginRight: '10px' }}
                />
                <MainButton
                    onClick={onNextClick1}
                    text="Next"
                    width="100px"
                    backgroundColor="#1f477d"
                    fontColor="#ffffff"
                    className="main-button"
                />
            </div>
        </div>
    )
}

function TemplateTypeListBuilder() {
    const IB = {
        value: "IB",
        label: "IB",
    }
    const CSR = {
        value: "CSR",
        label: "CSR",
    }
    const WrittenSummary = {
        value: "Written Summary",
        label: "Written Summary",
    }
    // 
    const PharmacologyTabulatedSummary = {
        value: "Pharmacology Tabulated Summary",
        label: "Pharmacology Tabulated Summary",
    }
    const ToxicologyTabulatedSummary = {
        value: "Toxicology Tabulated Summary",
        label: "Toxicology Tabulated Summary",
    }
    const PharmacoKineticTabulatedSummary = {
        value: "PharmacoKinetic Tabulated Summary",
        label: "PharmacoKinetic Tabulated Summary",
    }
    const CSRWithShellTable = {
        value: "CSR with Shell Table(s)",
        label: "CSR with Shell Table(s)",
    }
    return [IB, CSR, WrittenSummary, PharmacologyTabulatedSummary, ToxicologyTabulatedSummary, PharmacoKineticTabulatedSummary]
}

function CreateTemplatePage() {
    const theme = useTheme()
    // Step States
    const [step, setStep] = useState(START_STEP)

    // Template States
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    // Template Description States
    const [newTemplateName, setNewTemplateName] = useState('')
    const [templateType, setTemplateType] = useState('')
    const [description, setDescription] = useState('')

    // const [viewTemplateModal, setViewTemplateModal] = useState(false)
    const [fileUrl, setFileUrl] = useState('')

    // Snackbar States
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')

    const [processingTemplate, setProcessingTemplate] = useState(false)
    const [isDocumentTypeLoading, setIsDocumentTypeLoading] = useState(false)

    // Modal States
    const [templateSelectionModalOpen, setTemplateSelectionModalOpen] = useState(false)
    const [inputSelectionModalOpen, setInputSelectionModalOpen] = useState(false)
    const [outputSelectionModalOpen, setOutputSelectionModalOpen] = useState(false)
    const [fileBeingReplaced, setFileBeingReplaced] = useState('')
    const [trainingSetIndexBeingModified, setTrainingSetIndexBeingModified] = useState(-1)

    // - TODO: Delete this when confirmed that we don't need it
    //
    // const [leanWritingTemplateSelectionModalOpen, setLeanWritingTemplateSelectionModalOpen] = useState(false)

    // File States
    const [inputFiles, setInputFiles] = useState([])
    // const [outputFiles, setOutputFiles] = useState([])
    const [isLoadingLibraryFiles, setIsLoadingLibraryFiles] = useState(true)

    // Selected Files
    const [selectedTemplateFiles, setSelectedTemplateFiles] = useState([])
    const [selectedInputFiles, setSelectedInputFiles] = useState([])
    const [selectedOutputFiles, setSelectedOutputFiles] = useState([])
    const [selectedReplacedOutputFiles, setSelectedReplacedOutputFiles] = useState([])
    const [trainingSetFiles, setTrainingSetFiles] = useState([])

    // - TODO: Delete this when confirmed that we don't need it
    //
    // const [selectedLeanWritingTemplateFiles, setSelectedLeanWritingTemplateFiles ] = useState([])

    // Selected File Names
    const [selectedTemplateFileName, setSelectedTemplateFileName] = useState([])
    const [selectedInputFileNames, setSelectedInputFileNames] = useState([])
    const [selectedOutputFileNames, setSelectedOutputFileNames] = useState([])
    const [selectedReplacedOutputFileNames, setSelectedReplacedOutputFileNames] = useState([])
    const [selectedTrainingSetFileNames, setSelectedTrainingSetFileNames] = useState([])

    const { styleGuides } = [] // Removed because styleGuidescontext is not being used and causes rerender
    const [styleGuide, setStyleGuide] = useState('')

    const [success, setSuccess] = useState(false)

    // GeneratingTemplateModal state
    const [generatingTemplateModalOpen, setGeneratingTemplateModalOpen] = useState(false)
    const [status, setStatus] = useState('waiting') // 'waiting', 'generating', 'success', 'error'

    const [templateTypes, setTemplateTypes] = useState([])

    // Initialize styleGuide when styleGuides are loaded
    // useEffect(() => {
    //     if (styleGuides && styleGuides.length > 0 && !styleGuide) {
    //         setStyleGuide(styleGuides[0])
    //     }
    // }, [styleGuides, styleGuide])

    // Ensure context is synced when user navigates between steps
    useEffect(() => {
        // Sync all data when step changes to ensure context is up to date
        updateTemplateDetails({
            name: newTemplateName,
            description: description,
            type: templateType,
            styleGuide: styleGuide?.style_guide_name || styleGuide || '',
        })
        
        if (selectedTemplateFiles.length > 0) {
            updateReferenceFile(selectedTemplateFiles[0])
        }
        
        updateTrainingSets(trainingSetFiles)
    }, [step]) // Trigger when step changes

    // - TODO: Delete this when confirmed that we don't need it
    //
    // const [selectedLeanWritingTemplateFileNames, setSelectedLeanWritingTemplateFileNames] = useState([])

    const { isDemo } = useDemo()

    const navigate = useNavigate()
    const authInfo = useAuthInfo()
    const orgs = authInfo.orgHelper.getOrgs()
    const org = orgs[0].orgId

    const steps = [
        'Details',
        'Template File',
        'Example File(s)',
        'Review',
    ]

    // Context integration
    const { 
        updateTemplateDetails, 
        updateReferenceFile, 
        updateTrainingSets,
        addTrainingSet,
        templateDetails,
        referenceFile,
        trainingSets
    } = useTemplateCreation()

    // Sync data with context whenever relevant state changes
    useEffect(() => {
        updateTemplateDetails({
            name: newTemplateName,
            description: description,
            type: templateType,
            styleGuide: styleGuide?.style_guide_name || styleGuide || '',
        })
    }, [newTemplateName, description, templateType, styleGuide, updateTemplateDetails])

    useEffect(() => {
        if (selectedTemplateFiles.length > 0) {
            updateReferenceFile(selectedTemplateFiles[0])
        } else {
            updateReferenceFile(null)
        }
    }, [selectedTemplateFiles, updateReferenceFile])

    useEffect(() => {
        updateTrainingSets(trainingSetFiles)
    }, [trainingSetFiles])

    useEffect(() => {
        const fetchTemplateTypes = async () => {
            const orgAccessToken = await getOrgAccessToken(org)
            const payload = {
                organization_id: org
            }
            const templateTypesResponse = await getDocumentTypesForOrganization(orgAccessToken, payload)
            setTemplateTypes(templateTypesResponse.document_types)
        }
        fetchTemplateTypes()
    }, [])

    const validateStep = (currentStep) => {
        switch (currentStep) {
            case 0: // Details
                if (!newTemplateName.trim()) {
                    setSnackbarMessage(
                        'Please enter a Template Name before proceeding'
                    )
                    setSnackbarOpen(true)
                    return false
                }
                if (!templateType) {
                    setSnackbarMessage(
                        'Please select a Document Type before proceeding'
                    )
                    setSnackbarOpen(true)
                    return false
                }
                return true
                
            case 1: // Reference file
                if (selectedTemplateFiles.length === 0) {
                    setSnackbarMessage(
                        'Please select a Template File to upload before proceeding'
                    )
                    setSnackbarOpen(true)
                    return false
                }
                return true
                
            case 2: // Training sets
                return true
                

            case 3: // Review
                // All validation should have been done in previous steps
                handleGeneratingTemplateModalOpen()
                return true
                
            default:
                return true
        }
    }

    // Template Selection Modal
    const handleTemplateSelectionModalOpen = () => {
        setTemplateSelectionModalOpen(true)
    }

    const handleTemplateModalClose = () => {
        setTemplateSelectionModalOpen(false)
    }
    
    const handleTemplateSelect = (files) => {
        console.log(files)
        setSelectedTemplateFiles(files)
        setSelectedTemplateFileName(
            files.map((file) =>
                file.name.replace(`${orgs[0].orgId}/templates/`, '')
            )
        )
    }

    // Input Selection Modal
    const handleInputModalOpen = () => {
        setInputSelectionModalOpen(true)
    }
    const handleInputModalClose = () => {
        setInputSelectionModalOpen(false)
    }
    const handleInputSelect = (files) => {
        console.log(files)
        setSelectedInputFiles(files)
        setSelectedInputFileNames(
            files.map((file) =>
                file.name.replace(`${orgs[0].orgId}/templates/`, '')
            )
        )
    }

    // Output Selection Modal
    const handleOutputModalOpen = () => {
        console.log('[handleOutputSelect] handleOutputModalOpen')
        setOutputSelectionModalOpen(true)
    }

    const handleOutputModalOpenReplace = (fileToReplace = '', trainingSetIndex = -1) => {
        console.log('[handleOutputSelect] handleOutputModalOpenReplace')
        console.log('[handleOutputSelect] fileToReplace: ', fileToReplace)
        console.log('[handleOutputSelect] trainingSetIndex: ', trainingSetIndex)
        console.log('[handleOutputSelect] fileBeingReplaced: ', fileBeingReplaced)
        console.log('[handleOutputSelect] trainingSetIndexBeingModified: ', trainingSetIndexBeingModified)
        setFileBeingReplaced(fileToReplace)
        setTrainingSetIndexBeingModified(trainingSetIndex)
        setSelectedReplacedOutputFiles([fileToReplace])
        setSelectedReplacedOutputFileNames([fileToReplace.name.replace(`${orgs[0].orgId}/templates/`, '')])
        setOutputSelectionModalOpen(true)
    }

    const handleOutputModalClose = () => {
        console.log('[handleOutputSelect] handleOutputModalClose')
        setOutputSelectionModalOpen(false)
        setFileBeingReplaced('')
        setTrainingSetIndexBeingModified(-1)
    }

    const handleOutputSelect = (files) => {
        console.log('[handleOutputSelect] files: ', files)
        console.log('[handleOutputSelect] fileBeingReplaced: ', fileBeingReplaced)
        console.log('[handleOutputSelect] trainingSetIndexBeingModified: ', trainingSetIndexBeingModified)

        // If we're replacing a file in an existing training set
        if (fileBeingReplaced !== '' && trainingSetIndexBeingModified !== -1) {
            console.log('Replacing file in existing training set')

            // Check if user selected the same file (no change)
            if (files.length > 0 && fileBeingReplaced && fileBeingReplaced.name && files[0].name === fileBeingReplaced.name) {
                console.log('User selected the same file, no replacement needed')
                // Reset replacement state and close modal
                setSelectedReplacedOutputFiles(files)
                setSelectedReplacedOutputFileNames(
                    files.map((file) =>
                        file.name.replace(`${orgs[0].orgId}/templates/`, '')
                    )
                )
                // Close the modal by calling handleOutputModalClose
                handleOutputModalClose()
                return
            }

            // If user selected a different file, perform the replacement
            if (files.length > 0) {
                const newTrainingSetFiles = [...trainingSetFiles]
                newTrainingSetFiles[trainingSetIndexBeingModified] = {
                    ...newTrainingSetFiles[trainingSetIndexBeingModified],
                    outputFile: files[0]
                }
                setTrainingSetFiles(newTrainingSetFiles)
                console.log('File replaced successfully')
            }

            // Always reset replacement state after any action
            setSelectedReplacedOutputFiles(files)
            setSelectedReplacedOutputFileNames(
                files.map((file) =>
                    file.name.replace(`${orgs[0].orgId}/templates/`, '')
                )
            )
        } else {
            console.log('Normal output file selection for new training set')
            // Normal output file selection for new training set
            setSelectedOutputFiles(files)
            setSelectedOutputFileNames(
                files.map((file) =>
                    file.name.replace(`${orgs[0].orgId}/templates/`, '')
                )
            )
        }
    }

    // Fetch Files
    const fetchFiles = async (container_name) => {
        try {
            setIsLoadingLibraryFiles(true)
            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
            const files = await listAllFiles(container_name, orgAccessToken)
            setInputFiles(files)
        } catch (error) {
            console.error('Error fetching files:', error)
            setError(true)
        } finally {
            setIsLoadingLibraryFiles(false)
        }
    }

    useEffect(() => {
        fetchFiles('input')
        // - TODO: Uncomment this when existance of output container is confirmed
        //
        // fetchFiles('output')
    }, [])

    const handleFilesUploaded = () => {
        fetchFiles('input')
    }

    // Form Completion Functions
    const templateInputComplete = async () => {
        if (validateStep(0)) {
            const orgAccessToken = await getOrgAccessToken(org)
            console.log('org: ', org)
            console.log('orgAccessToken: ', orgAccessToken)

            console.log('selectedTemplateFiles: ', removeOrgId(selectedTemplateFiles[0].name, org))

            try {
                await ingestTemplate (
                    newTemplateName,
                    removeOrgId(selectedTemplateFiles[0].name, org),
                    orgAccessToken,
                    setLoading
                )
            } catch (error) {
                setError(true)
                setSnackbarMessage('Error ingesting template')
                setSnackbarOpen(true)
            } finally {
                setStep(1)
            }
        }
    }

    // Form Completion Mock Function
    const mockTemplateInputComplete = async () => {
        if(validateStep(0)) {
            setProcessingTemplate(true)
            setLoading(true)
            const orgAccessToken = await getOrgAccessToken(org)

            try {
                setTimeout(() => {
                    setStep(1)
                    setLoading(false)
                    setProcessingTemplate(false)
                }, 4000)
            } catch (error) {
                setError(true)
                setSnackbarMessage('Error ingesting template')
                setSnackbarOpen(true)
            }
        }
    }
    
    const handleGeneratingTemplateModalOpen = () => {
        setStatus('generating')
        setGeneratingTemplateModalOpen(true)
    }

    const handleGeneratingTemplateModalClose = async () => {
        // Reset states and navigate immediately
        setStatus('waiting')
        setSuccess(false)
        setError(false)
        setLoading(false)
        setGeneratingTemplateModalOpen(false)
        navigate(`/templates?success=true`)
    }

    const systemInputComplete = async () => {
        if (validateStep(1)) {
            setStep(2)
            const orgAccessToken = await getOrgAccessToken(org)

            // if (org === 'c3c7c741-e38b-4dbb-b8c0-be8367d36b7f') {
            //     // setSystemInputView(false)
            //     // setTextInputView(false)
            //     var params = { id: 'CSR' }
            //     navigateToDocumentWithUrl(params)
            // } else {
            // setSystemInputView(false)
            // setTextInputView(false)
            setProcessingTemplate(true)
            // console.log('templateType: ', templateType)
            // console.log('selectedTemplateFileName: ', selectedTemplateFileName)
            // console.log('description: ', description)

            console.log('trainingSetFiles: ', trainingSetFiles)
            console.log(
                'trainingSetFiles.inputFiles: ',
                trainingSetFiles[0].inputFiles
            )
            console.log('trainingSetFiles[0].outputFile: ', trainingSetFiles[0].outputFile)

            const doc_set_key = syncRemoveFileExtension(formatFilename(trainingSetFiles[0].outputFile.name))
            
            console.log('orgAccessToken: ', orgAccessToken)
            console.log('doc_set_key: ', doc_set_key)

            try {
                console.log('trainingSetFiles: ', trainingSetFiles)
                console.log('trainingSetFiles.inputFiles: ', trainingSetFiles[0].inputFiles)

                const ingestSourceDocumentsResponse = await ingestSourceDocuments(
                    trainingSetFiles[0].inputFiles.map((file) => file.name),
                    doc_set_key,
                    orgAccessToken,
                    setLoading
                )

                console.log(
                    'ingestSourceDocumentsResponse: ',
                    ingestSourceDocumentsResponse.azure_index
                )

                const azure_index = ingestSourceDocumentsResponse.azure_index

                console.log('azure_index: ', azure_index)
                console.log('trainingSetFiles[0].outputFile: ', trainingSetFiles[0].outputFile)

                const analyzeExampleDocumentResponse = await analyzeExampleDocument(
                    trainingSetFiles[0].outputFile.name,
                    azure_index,
                    doc_set_key,
                    orgAccessToken,
                    setLoading
                )

                console.log(
                    'analyzeExampleDocumentResponse: ',
                    analyzeExampleDocumentResponse
                )

                navigate('/templates?success=true')
            } catch (error) {
                setError(true)
                setSnackbarMessage('Error processing template')
                setSnackbarOpen(true)
            } finally {
                setProcessingTemplate(false)
            }
            // }
        }
    }

    const mockSystemInputComplete = async () => {
        if(validateStep(3)) {
            setProcessingTemplate(true)
            setLoading(true)
            
            try {
                setTimeout(() => {
                    setLoading(false)
                    setProcessingTemplate(false)
                    navigate('/templates?success=true')
                }, 4000)
            } catch (error) {
                setError(true)
                setSnackbarMessage('Error processing template')
                setSnackbarOpen(true)
            } 
        }
    }

    const handleTabulatedSummaryInputComplete = async (orgAccessToken) => {
        console.log('Creating tabulated summary template')

        // Training sets have outputFile which represents the example document
        const exampleFilenames = trainingSetFiles.map(trainingSet => {
            const outputFileName = trainingSet.outputFile?.name || trainingSet.outputFile
            return outputFileName.split('/').slice(1).join('/')
        })
        console.log('exampleFilenames: ', exampleFilenames)

        const shellInputPaths = selectedTemplateFiles[0].name.split('/').slice(1).join('/')
        console.log('shellInputPaths: ', shellInputPaths)

        const payload = {
            example_input_paths: exampleFilenames,
            shell_input_paths: [shellInputPaths],
            document_name: newTemplateName.trim(),
            document_type: templateType
        }
        console.log('Tabulated summary payload: ', payload)

        const ingestTabulatedSummaryExamplesResponse = await ingestTabulatedSummaryExamples(
            orgAccessToken,
            payload
        )
        console.log('ingestTabulatedSummaryExamplesResponse: ', ingestTabulatedSummaryExamplesResponse)
    }

    const handleOtherDocumentTypeInputComplete = async (orgAccessToken) => {
        // Training sets have outputFile which represents the example document
        const exampleFilenames = trainingSetFiles.map(trainingSet => {
            const outputFileName = trainingSet.outputFile?.name || trainingSet.outputFile
            return outputFileName.split('/').slice(1).join('/')
        })

        const cache_version = String(uuidv4())
        
        const payload = {
            name: newTemplateName.trim(),
            template_filename: selectedTemplateFiles[0].name.split('/').slice(1).join('/'),
            description: description,
            document_type: templateType,
            example_filenames: exampleFilenames.length > 0 ? exampleFilenames : [""],
            cache_version: cache_version
        }

        console.log('Creating template with payload:', payload)

        const createGenericMRTOutlineAsyncResponse = await createGenericMRTOutlineAsync(
            orgAccessToken,
            payload
        )
    
        console.log('createGenericMRTOutlineAsyncResponse:', createGenericMRTOutlineAsyncResponse)
    }
    
    // New function to handle review confirmation
    const handleReviewConfirm = async () => {
        setLoading(true)
        setGeneratingTemplateModalOpen(true) // Open modal directly

        // Set up 10-second timeout to navigate even if request hasn't completed
        const timeoutId = setTimeout(() => {
            setStatus('success')
            setSuccess(true)
            setLoading(false)
            
            // Auto-close modal after 3 seconds
            setTimeout(() => {
                setGeneratingTemplateModalOpen(false)
                // Navigate to templates page with success flag
                navigate('/templates?success=true')
            }, 3000)
        }, 10000) // 10 seconds

        try {
            console.log('templateType: ', templateType)
            
            // Get the org access token
            const orgAccessToken = await getOrgAccessToken(org)
            
            if (templateType.includes('Tabulated Summary')) {
                await handleTabulatedSummaryInputComplete(orgAccessToken)
            } else {
                await handleOtherDocumentTypeInputComplete(orgAccessToken)
            }
            
            // Clear the timeout if request completes before 10 seconds
            clearTimeout(timeoutId)
            
            setStatus('success')
            setSuccess(true)
            setLoading(false)
            
            
            // Auto-close modal after 30 seconds
            // setTimeout(() => {
            //     setGeneratingTemplateModalOpen(false)
            //     // Navigate immediately on successful response
            //     navigate('/templates?success=true')
            // }, 30000)
            
        } catch (error) {
            // Clear the timeout on error as well
            clearTimeout(timeoutId)
            
            setError(true)
            setStatus('error')
            setSnackbarMessage('Error creating template')
            setSnackbarOpen(true)
        }
    }

    const onOutputFileSubmit = async () => {
        if (validateStep(1)) {
            setStep(2)

            const orgAccessToken = await getOrgAccessToken(org)

            console.log('orgAccessToken: ', orgAccessToken)

            try {
                const azure_index = "df48b598-6401-4d20-888f-97a6e3abbfa5-temp-docs-d991559f-11b5-4257-bb12-ed79265ba42a"
                const doc_set_key = syncRemoveFileExtension(
                    formatFilename(trainingSetFiles[0].outputFile.name)
                )

                const analyzeExampleDocumentResponse = await analyzeExampleDocument(
                    trainingSetFiles[0].outputFile.name,
                    azure_index,
                    doc_set_key,
                    orgAccessToken,
                    setLoading
                )

                console.log(
                    'analyzeExampleDocumentResponse: ',
                    analyzeExampleDocumentResponse
                )

                navigate('/templates?success=true')
            } catch (error) {
                setError(true)
                setSnackbarMessage('Error processing template')
                setSnackbarOpen(true)
            } finally {
                setProcessingTemplate(false)
            }
        }
    }

    // Back Button Clicks
    const createTrainingSetBackClick = () => {
        setStep(0)
    }

    async function getOrgAccessToken(orgId) {
        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
        return orgAccessToken
    }

    // const navigateToDocumentWithUrl = (params) => {
    //     navigate(`/editTemplate/${params.id}`)
    // }

    // const handleStepChange = (direction) => {
    //     const newStep = step + direction
    //     if (newStep >= 0 && newStep < steps.length) {
    //         if (direction > 0 && !validateStep(step)) {
    //             return
    //         }
    //         setStep(newStep)
    //     }
    // }

    useEffect(() => {
        if (success) {
            setSuccess(false)
        }
    }, [success])

    const handleSnackbarClose = () => {
        setSnackbarOpen(false)
    }

    const handleAddTrainingSet = () => {
        // Prevent creating training sets without output files
        if (selectedOutputFiles.length === 0) {
            setSnackbarMessage('Please select an output file before adding a training set')
            setSnackbarOpen(true)
            return
        }

        console.log('selectedOutputFiles: ', selectedOutputFiles)
        console.log('selectedInputFiles: ', selectedInputFiles)
        console.log('trainingSetFiles: ', trainingSetFiles)

        addTrainingSet({
            id: uuidv4(),
            outputFile: selectedOutputFiles[0],
            inputFiles: [...selectedInputFiles]
        })

        setTrainingSetFiles([...trainingSetFiles, {
            id: uuidv4(),
            outputFile: selectedOutputFiles[0],
            inputFiles: [...selectedInputFiles]
        }])

        setSelectedTrainingSetFileNames([...selectedTrainingSetFileNames, {
            id: uuidv4(),
            outputFile: selectedOutputFileNames[0],
            inputFiles: [...selectedInputFileNames]
        }])
        setSelectedInputFiles([])
        setSelectedOutputFiles([])
        setSelectedInputFileNames([])
        setSelectedOutputFileNames([])
        setFileBeingReplaced('')
        setTrainingSetIndexBeingModified(-1)
    }

    const handleDeleteTrainingSet = (setId) => {
        const newTrainingSetFiles = trainingSetFiles.filter(
            (file) => file.id !== setId
        )
        setTrainingSetFiles(newTrainingSetFiles)
        
        // Also update the corresponding file names array
        const newTrainingSetFileNames = selectedTrainingSetFileNames.filter(
            (file) => file.id !== setId
        )
        setSelectedTrainingSetFileNames(newTrainingSetFileNames)
    }

    const handleRemoveInputFile = (fileId) => {
        const newInputFiles = selectedInputFiles.filter(
            (file) => file.id !== fileId
        )
        setSelectedInputFiles(newInputFiles)
    }
    

    const handleDiscardCurrentTrainingSet = () => {
        setSelectedInputFileNames([])
        setSelectedOutputFileNames([])
        setSelectedInputFiles([])
        setSelectedOutputFiles([])
    }

    useEffect(() => {
        console.log('selectedTemplateFiles: ', selectedTemplateFiles)
    }, [selectedTemplateFiles])

    const ProcessingTemplateSkeleton = () => {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    width: '100%',
                    gap: '24px',
                }}
            >
                <CircularProgress
                    size={48}
                    sx={{
                        color: '#6941C6',
                    }}
                />
                <Typography
                    variant="h6"
                    sx={{
                        color: '#101828',
                        fontSize: '18px',
                        fontWeight: 500,
                        lineHeight: '28px',
                    }}
                >
                    Processing template upload...
                </Typography>
            </div>
        )
    }

    const renderStepContent = () => {
        if (processingTemplate) {
            return (
                <ProcessingTemplateSkeleton />
            )
        }
        

        return (
            <Fade
                in={true}
                mountOnEnter
                unmountOnExit
                timeout={{ enter: 450, exit: 0 }}
                easing={{
                    enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <div>
                    {step === 0 && (
                        <TemplateDetailsComponent
                            templateName={newTemplateName}
                            handleTemplateNameChange={e => {
                                e.stopPropagation()
                                setNewTemplateName(e.target.value)
                            }}
                            description={description}
                            handleDescriptionChange={e => {
                                e.stopPropagation()
                                setDescription(e.target.value)
                            }}
                            templateType={templateType}
                            setTemplateType={setTemplateType}
                            onNextClick={() => {
                                if (validateStep(0)) {
                                    setStep(1)
                                }
                            }}
                            categories={templateTypes}
                            styleGuides={styleGuides}
                            styleGuide={styleGuide}
                            setStyleGuide={setStyleGuide}
                            isDocumentTypeLoading={isDocumentTypeLoading}
                        />
                    )}
                    {step === 1 && (
                        <ReferenceFileComponent
                            onNextClick={() => {
                                if (validateStep(1)) {
                                    setStep(2)
                                }
                            }}
                            onBackClick={() => setStep(0)}
                            onFileSelect={handleTemplateSelectionModalOpen}
                            selectedFiles={selectedTemplateFiles}
                            removeFile={(index) => {
                                const newFiles = [...selectedTemplateFiles]
                                newFiles.splice(index, 1)
                                setSelectedTemplateFiles(newFiles)
                            }}
                        />
                    )}
                    {step === 2 && (
                        <TrainingSetCreationComponent
                            onNextClick1={() => {
                                setStep(3)
                            }}
                            onBackClick={() => setStep(1)}
                            onAddTrainingSetClick={handleAddTrainingSet}
                            onDiscardTrainingSetClick={handleDiscardCurrentTrainingSet}
                            trainingSetFiles={trainingSetFiles}
                            setTrainingSetFiles={setTrainingSetFiles}
                            onInputFileSelect={handleInputModalOpen}
                            onOutputFileSelect={handleOutputModalOpen}
                            onReplaceFile={handleOutputModalOpenReplace}
                            inputFileList={selectedInputFiles}
                            outputFileList={selectedOutputFiles}
                            removeInputFile={(index) => {
                                const newFiles = [...selectedInputFiles]
                                newFiles.splice(index, 1)
                                setSelectedInputFiles(newFiles)
                            }}
                            removeOutputFile={(index) => {
                                const newFiles = [...selectedOutputFiles]
                                newFiles.splice(index, 1)
                                setSelectedOutputFiles(newFiles)
                            }}
                            orgId={orgs[0].orgId}
                            integrations={integrations}
                            loading={loading}
                            setLoading={setLoading}
                        />
                    )}
                    {step === 3 && (
                        <TemplateReviewComponent
                            onBackClick={() => setStep(2)}
                            onConfirm={handleReviewConfirm}
                            loading={loading}
                            setLoading={setLoading}
                            success={success}
                            setSuccess={setSuccess}
                            trainingSetFiles={trainingSetFiles}
                            referenceFile={referenceFile || selectedTemplateFiles[0]}
                            templateDetails={templateDetails}
                        />
                    )}
                </div>
            </Fade>
        )
    }

    return (
        <div
            className="full-width-container"
            style={{ backgroundColor: '#F9FAFB' }}
        >
            <div className="inner-container" style={{ padding: 0 }}>
                <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
                    {/* Left Sidebar with Stepper */}
                    {/* <Box
                        sx={{
                            width: '280px',
                            minWidth: '280px',
                            backgroundColor: '#F9FAFB',
                            padding: '40px 16px 40px 32px',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <CustomStepper 
                            activeStep={step} 
                            steps={steps} 
                            orientation="vertical" 
                        />
                    </Box> */}

                    {/* Right Content Area */}
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: '#F9FAFB',
                            overflowY: 'auto',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <Box sx={{ 
                            width: '100%', 
                            height: '100%', 
                            padding: '40px 64px',
                            '@media (max-width: 768px)': {
                                padding: '24px 16px',
                            },
                            position: 'relative' 
                        }}>
                            {/* Fixed positioned stepper that matches step content positioning */}
                            <Box sx={{
                                position: 'fixed',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: '104px', // Position below header
                                zIndex: 998, // Below step content but above main content
                                width: 'clamp(600px, 75vw, 1200px)',
                                '@media (max-width: 768px)': {
                                    width: 'calc(75vw - 16px)',
                                    minWidth: '280px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    top: '80px',
                                },
                                backgroundColor: '#F9FAFB',
                                paddingBottom: '24px',
                            }}>
                                <HorizontalStepper steps={steps} activeStep={step} />
                            </Box>
                            {renderStepContent()}
                        </Box>
                    </Box>
                </Box>
            </div>

            {/* Template Selection Modal */}
            <Modal
                open={templateSelectionModalOpen}
                onClose={handleTemplateModalClose}
                aria-labelledby="source-selection-modal"
                aria-describedby="source-selection-description"
            >
                <div className="modal-background">
                    <div className="modal-content">
                        <SourceSelectionModal
                            open={templateSelectionModalOpen}
                            handleClose={handleTemplateModalClose}
                            onSourceSelect={handleTemplateSelect}
                            inputFiles={inputFiles}
                            isLoadingInputFiles={isLoadingLibraryFiles}
                            orgId={orgs[0].orgId}
                            org_name={orgs[0].orgName}
                            isMultiSelect={false}
                            initialSelectedFiles={selectedTemplateFiles.map(
                                (file) => ({
                                    ...file,
                                    url: inputFiles.find(
                                        (f) => f.name === file.name
                                    )?.url,
                                })
                            )}
                            getOrgAccessToken={() =>
                                getOrgAccessToken(orgs[0].orgId)
                            }
                            onFilesUploaded={handleFilesUploaded}
                            // allowedFolders={['templates', 'input', 'output']}
                            allowedFolders={['templates']}
                            allowedSelectedFileTypes={['docx']}
                        />
                    </div>
                </div>
            </Modal>
            {/* Input Selection Modal */}
            <Modal
                open={inputSelectionModalOpen}
                onClose={handleInputModalClose}
                aria-labelledby="source-selection-modal"
                aria-describedby="source-selection-description"
            >
                <div className="modal-background">
                    <div className="modal-content">
                        <SourceSelectionModal
                            open={inputSelectionModalOpen}
                            handleClose={handleInputModalClose}
                            onSourceSelect={handleInputSelect}
                            inputFiles={inputFiles}
                            isLoadingInputFiles={isLoadingLibraryFiles}
                            documentName={newTemplateName}
                            documentType={templateType}
                            orgId={orgs[0].orgId}
                            org_name={orgs[0].orgName}
                            isMultiSelect={true}
                            selectedFileNames={selectedInputFileNames}
                            initialSelectedFiles={selectedInputFiles.map(
                                (file) => ({
                                    ...file,
                                    url: inputFiles.find(
                                        (f) => f.name === file.name
                                    )?.url,
                                })
                            )}
                            getOrgAccessToken={() =>
                                getOrgAccessToken(orgs[0].orgId)
                            }
                            onFilesUploaded={handleFilesUploaded}
                            // allowedFolders={['input', 'output', 'templates']}
                            allowedFolders={['input']}
                        />
                    </div>
                </div>
            </Modal>
            {/* Output Selection Modal */}
            <Modal
                open={outputSelectionModalOpen}
                onClose={handleOutputModalClose}
                aria-labelledby="source-selection-modal"
                aria-describedby="source-selection-description"
            >
                <div className="modal-background">
                    <div className="modal-content">
                        <SourceSelectionModal
                            open={outputSelectionModalOpen}
                            handleClose={handleOutputModalClose}
                            onSourceSelect={handleOutputSelect}
                            inputFiles={inputFiles}
                            isLoadingInputFiles={isLoadingLibraryFiles}
                            documentName={newTemplateName}
                            documentType={templateType}
                            orgId={orgs[0].orgId}
                            org_name={orgs[0].orgName}
                            isMultiSelect={false}
                            selectedFileNames={
                                fileBeingReplaced !== ''
                                    ? selectedReplacedOutputFileNames
                                    : selectedOutputFileNames
                            }
                            initialSelectedFiles={
                                fileBeingReplaced !== ''
                                    ? selectedReplacedOutputFiles.map((file) => ({
                                        ...file,
                                        url: inputFiles.find(
                                            (f) => f.name === file.name
                                        )?.url,
                                    }))
                                    : selectedOutputFiles.map(
                                        (file) => ({
                                            ...file,
                                            url: inputFiles.find(
                                                (f) => f.name === file.name
                                            )?.url,
                                        })
                                    )
                            }
                            getOrgAccessToken={() =>
                                getOrgAccessToken(orgs[0].orgId)
                            }
                            onFilesUploaded={handleFilesUploaded}
                            // allowedFolders={['output', 'input', 'templates']}
                            allowedFolders={['input']}
                            allowedSelectedFileTypes={['docx', 'pdf']}
                        />
                    </div>
                </div>
            </Modal>
            <GeneratingTemplateModal
                open={generatingTemplateModalOpen}
                onClose={handleGeneratingTemplateModalClose}
                loading={loading || status === 'generating'}
                success={success || status === 'success'}
                error={status === 'error'}
                errorMessage={snackbarMessage}
                onSuccess={() => {
                    // navigate('/templates?success=true')
                }}
                onError={() => {
                    // Error handling is done in handleReviewConfirm
                }}
            />
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={error ? 'error' : 'success'}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    )
}

// Wrapper component with TemplateCreationProvider
function CreateTemplatePageWithProvider() {
    return (
        <TemplateCreationProvider>
            <CreateTemplatePage />
        </TemplateCreationProvider>
    )
}

export default CreateTemplatePageWithProvider
